import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Einreise.css';

const API_BASE = process.env.REACT_APP_API_BASE;

const Einreise = ({ userData }) => {
    const [discordId, setDiscordId] = useState('');
    const [fivemName, setFivemName] = useState('');
    const [status, setStatus] = useState(null);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [entries, setEntries] = useState([]);
    const [entriesLoading, setEntriesLoading] = useState(true);

    const fetchEntries = async () => {
        try {
            const token = localStorage.getItem('discord_token');
            const response = await axios.get(`${API_BASE}/api/immigration`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = Array.isArray(response.data) ? response.data : (response.data.entries || []);
            const sorted = data.sort((a, b) => new Date(b.timestamp || b.created_at) - new Date(a.timestamp || a.created_at));
            setEntries(sorted);
        } catch (error) {
            console.error('Error fetching entries:', error);
        } finally {
            setEntriesLoading(false);
        }
    };

    useEffect(() => {
        fetchEntries();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!discordId) {
            setStatus('error');
            setMessage('Bitte eine InGame ID eingeben.');
            return;
        }

        const intDiscordId = parseInt(discordId, 10);
        if (isNaN(intDiscordId) || intDiscordId < 1 || intDiscordId > 1000) {
            setStatus('error');
            setMessage('Die InGame ID muss zwischen 1 und 1000 liegen.');
            return;
        }

        if (!fivemName) {
            setStatus('error');
            setMessage('Bitte einen FiveM Namen eingeben.');
            return;
        }

        setLoading(true);
        setStatus(null);
        setMessage('');

        try {
            const token = localStorage.getItem('discord_token');
            await axios.post(`${API_BASE}/api/immigration`, {
                playerId: discordId,
                playerName: fivemName,
                teamlerId: userData.id
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setStatus('success');
            setMessage('Einreise erfolgreich eingetragen!');
            setDiscordId('');
            setFivemName('');
            fetchEntries();
        } catch (error) {
            console.error('Error submitting immigration entry:', error);
            setStatus('error');
            setMessage(error.response?.data?.error || 'Fehler beim Speichern der Einreise.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="dashboard-main">
            <div className="einreise-container">
                <div className="einreise-card">
                    <div className="einreise-header">
                        <div className="einreise-header-icon">
                            <i className="fas fa-plane-arrival"></i>
                        </div>
                        <h2>🛬 Einreise eintragen</h2>
                    </div>
                    <div className="einreise-body">
                        <p className="einreise-description">
                            Hier kannst du Spieler eintragen, die du eingereist hast.
                        </p>

                        <form onSubmit={handleSubmit} className="einreise-form">
                            <div className="einreise-form-group">
                                <label className="einreise-label">InGame ID des Spielers</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="1000"
                                    className="einreise-input"
                                    placeholder="z.B. 1, 2 oder 1000"
                                    value={discordId}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '' || (parseInt(val, 10) >= 1 && parseInt(val, 10) <= 1000)) {
                                            setDiscordId(val);
                                        }
                                    }}
                                    disabled={loading}
                                />
                            </div>

                            <div className="einreise-form-group">
                                <label className="einreise-label">FiveM Name des Spielers</label>
                                <input
                                    type="text"
                                    className="einreise-input"
                                    placeholder="Anzeige Namen eingeben"
                                    value={fivemName}
                                    onChange={(e) => setFivemName(e.target.value)}
                                    disabled={loading}
                                />
                            </div>

                            {status === 'success' && (
                                <div className={`einreise-alert einreise-alert-${status}`}>
                                    {message}
                                </div>
                            )}

                            {status === 'error' && (
                                <div className={`einreise-alert einreise-alert-${status}`}>
                                    {message}
                                </div>
                            )}

                            <div className="einreise-form-actions">
                                <button
                                    type="submit"
                                    className="einreise-submit-btn"
                                    disabled={loading}
                                >
                                    {loading ? 'Speichere...' : 'Eintragen'}
                                </button>
                            </div>
                        </form>

                        <div className="einreise-entries-section">
                            <div className="einreise-entries-header">
                                <i className="fas fa-list einreise-entries-icon"></i>
                                <h3 className="einreise-entries-title">Letzten 30 Einreisen</h3>
                                <span className="einreise-entries-subtitle">
                                    - Vorerst eine kleine Übersicht. Dies wird noch weiter überarbeitet.
                                </span>
                            </div>

                            {entriesLoading ? (
                                <div className="einreise-loading">
                                    <div className="spinner"></div>
                                    <p>Lade Einträge...</p>
                                </div>
                            ) : entries.length === 0 ? (
                                <div className="einreise-empty">
                                    <p style={{ margin: 0 }}>Keine Einträge in der Datenbank gefunden.</p>
                                </div>
                            ) : (
                                <div className="einreise-table-container">
                                    <table className="einreise-table">
                                        <thead>
                                            <tr>
                                                <th>
                                                    <i className="far fa-clock"></i>
                                                    Zeitpunkt
                                                </th>
                                                <th>
                                                    <i className="fab fa-discord"></i>
                                                    InGame ID
                                                </th>
                                                <th>
                                                    <i className="fas fa-user"></i>
                                                    FiveM Name
                                                </th>
                                                <th>
                                                    <i className="fas fa-user-tag"></i>
                                                    Eingereicht von
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {entries.slice(0, 30).map((entry, index) => (
                                                <tr key={entry.id || index}>
                                                    <td>
                                                        <span className="einreise-timestamp">
                                                            {new Date(entry.timestamp || entry.created_at).toLocaleString('de-DE', {
                                                                day: '2-digit',
                                                                month: '2-digit',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <code className="einreise-player-id">
                                                            {entry.playerId || entry.player_id}
                                                        </code>
                                                    </td>
                                                    <td>
                                                        <span className="einreise-player-name">
                                                            {entry.player_name || '-'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {(() => {
                                                            const isCurrentUserEntry = entry.teamler_id === userData?.id;
                                                            const currentUserIsCreator = Array.isArray(userData?.roles) && userData.roles.includes('1492514375905316940');

                                                            if (isCurrentUserEntry && currentUserIsCreator) {
                                                                return (
                                                                    <span className="einreise-creator-tag">
                                                                        <i className="fas fa-check-circle" style={{ color: '#00ffc6', marginRight: '6px' }}></i>
                                                                        {entry.teamler_display_name || entry.teamler_id}
                                                                        <code className="einreise-player-id" style={{ marginLeft: '6px' }}>
                                                                            {entry.teamler_id}
                                                                        </code>
                                                                    </span>
                                                                );
                                                            }

                                                            if (isCurrentUserEntry) {
                                                                return (
                                                                    <span className="einreise-creator-tag">
                                                                        {entry.teamler_display_name || entry.teamler_id}
                                                                        <code className="einreise-player-id" style={{ marginLeft: '6px' }}>
                                                                            {entry.teamler_id}
                                                                        </code>
                                                                    </span>
                                                                );
                                                            }

                                                            return (
                                                                <span className="muted">
                                                                    {entry.teamler_display_name || entry.teamler_id}
                                                                    <code className="einreise-player-id" style={{ marginLeft: '6px' }}>
                                                                        {entry.teamler_id}
                                                                    </code>
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Einreise;
